import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Button, Icon, IconNames } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import { Container as AccountSelectorWrapper, ChipsArea, ProfileChip, ProfileAvatarWrapper, OverflowChip, OverflowPanel } from './index.styles';
import { useReportingContext } from '../../context';
import { PLATFORM_COLOR_ICON_MAP } from '../../helpers';
import { useGetProfiles } from '../../../../models/reporting';
import { ProfilePickerDropdown } from '../ProfilePickerDropdown';

interface AccountSelectorProps {
  disabled?: boolean;
  showPicker?: boolean;
}

const OVERFLOW_THRESHOLD = 5;

export const AccountSelector = ({ disabled = false, showPicker = true }: AccountSelectorProps) => {
  const { t } = useTranslation('reports');
  const { profiles, setProfiles } = useReportingContext();

  const { data: apiProfiles, isLoading } = useGetProfiles();

  const allAccounts: ReportProfile[] = apiProfiles ?? [];

  const handleRemoveProfile = (profileId: string) => {
    setProfiles(profiles.filter((p) => p.id !== profileId));
  };

  const [failedAvatarIds, setFailedAvatarIds] = useState<Record<string, true>>({});
  const [showOverflowPanel, setShowOverflowPanel] = useState(false);

  // ── Render-Phase Synchronization ──
  // If profiles drop below threshold, update state inline during render pass
  if (profiles.length <= OVERFLOW_THRESHOLD && showOverflowPanel) {
    setShowOverflowPanel(false);
  }

  const markAvatarFailed = (id: string) => {
    setFailedAvatarIds((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  const overflowChipRef = useRef<HTMLDivElement>(null);
  const overflowPanelRef = useRef<HTMLDivElement | null>(null);

  const { x, y, strategy, refs } = useFloating({
    open: showOverflowPanel,
    onOpenChange: setShowOverflowPanel,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideChip = !overflowChipRef.current || !overflowChipRef.current.contains(target);
      const isOutsidePanel = !overflowPanelRef.current || !overflowPanelRef.current.contains(target);

      if (isOutsideChip && isOutsidePanel) {
        setShowOverflowPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const visibleProfiles = profiles.slice(0, OVERFLOW_THRESHOLD);
  const overflowProfiles = profiles.slice(OVERFLOW_THRESHOLD);
  const isOverflow = overflowProfiles.length > 0;

  const renderProfileAvatar = (profile: ReportProfile) => {
    if (profile.isUnknown) {
      return null;
    }

    const networkIcon = (PLATFORM_COLOR_ICON_MAP[profile.platform] ?? 'user') as IconNames;
    const canShowImg = !!profile.avatar && !failedAvatarIds[profile.id];
    const initial = (profile.name?.trim()?.[0] ?? '?').toUpperCase();

    return (
      <ProfileAvatarWrapper>
        <div className="avatar-img">
          {canShowImg ? (
            <img
              src={profile.avatar}
              alt={profile.name?.trim() || ''}
              onError={() => markAvatarFailed(profile.id)}
            />
          ) : (
            <p>{initial}</p>
          )}
        </div>
        <div className="network-badge">
          <Icon name={networkIcon} />
        </div>
      </ProfileAvatarWrapper>
    );
  };

  const dropdownRoot = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;

  return (
    <AccountSelectorWrapper>
      {showPicker && (
        <ProfilePickerDropdown
          allProfiles={allAccounts}
          selectedProfiles={profiles.filter((p) => !p.isUnknown)}
          onSelect={disabled ? () => undefined : (selected) => setProfiles([...selected, ...profiles.filter((p) => p.isUnknown)])}
          disabled={disabled}
          isLoading={isLoading}
        />
      )}

      {profiles.length > 0 && (
        <ChipsArea data-testid="profile-chips-area">
          {visibleProfiles.map((profile) => (
            <ProfileChip
              key={profile.id}
              data-testid="profile-chip"
              $isUnknown={profile.isUnknown}
            >
              {renderProfileAvatar(profile)}
              <p>{profile.isUnknown ? `Profile(${profile.id})` : profile.name}</p>
              <Button
                data-testid="profile-chip-remove"
                icon="close"
                onClick={() => handleRemoveProfile(profile.id)}
                disabled={disabled}
                size={12}
              />
            </ProfileChip>
          ))}

          {isOverflow && (
            <OverflowChip
              ref={(node) => {
                overflowChipRef.current = node;
                refs.setReference(node); // Attaches ref safely to the native container boundary element
              }}
              data-testid="profile-chip-overflow"
            >
              <button
                data-testid="profile-chip-overflow-toggle"
                onClick={() => setShowOverflowPanel((prev) => !prev)}
                type="button"
              >
                +{overflowProfiles.length}
              </button>
            </OverflowChip>
          )}

          <Button
            customClass="clear-button"
            data-testid="profile-chips-clear-all"
            label={t('new_reports_accounts_clear_all') as string}
            onClick={() => {
              setProfiles([]);
              setShowOverflowPanel(false);
            }}
            disabled={disabled}
            size={12}
          />
        </ChipsArea>
      )}

      {isOverflow &&
        showOverflowPanel &&
        dropdownRoot &&
        createPortal(
          <OverflowPanel
            ref={(node) => {
              refs.setFloating(node);
              overflowPanelRef.current = node;
            }}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
            data-testid="profile-chip-overflow-panel"
          >
            {overflowProfiles.map((profile) => (
              <ProfileChip
                key={profile.id}
                data-testid={`overflow-profile-chip-${profile.id}`}
                $isUnknown={profile.isUnknown}
              >
                {renderProfileAvatar(profile)}
                <p>{profile.isUnknown ? `Profile(${profile.id})` : profile.name}</p>
                <Button
                  data-testid={`overflow-profile-chip-remove-${profile.id}`}
                  icon="close"
                  onClick={() => handleRemoveProfile(profile.id)}
                  disabled={disabled}
                  size={12}
                />
              </ProfileChip>
            ))}
          </OverflowPanel>,
          dropdownRoot
        )}
    </AccountSelectorWrapper>
  );
};
